import React, { useState, useRef, useEffect } from 'react';

/**
 * OTP EMAIL SENDER - PART 5
 *
 * Send plain email with code
 * See EVERYTHING about email delivery
 */

interface EmailEvent {
  id: string;
  timestamp: number;
  layer: 'validation' | 'api' | 'smtp' | 'mx' | 'delivery' | 'tracking';
  type: 'info' | 'success' | 'error' | 'process' | 'detail' | 'warning';
  message: string;
  details?: any;
  duration?: number;
}

interface EmailRecord {
  id: string;
  to: string;
  from: string;
  subject: string;
  code: string;
  status: 'pending' | 'sending' | 'sent' | 'delivered' | 'failed' | 'bounced';
  sentAt?: Date;
  deliveredAt?: Date;
  provider: string;
}

export default function OTPEmailSender() {
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [toEmail, setToEmail] = useState('user@example.com');
  const [otpCode, setOtpCode] = useState('');
  const [emailHistory, setEmailHistory] = useState<EmailRecord[]>([]);
  const [showRawEmail, setShowRawEmail] = useState(false);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (event: Omit<EmailEvent, 'id' | 'timestamp'>) => {
    const newEvent: EmailEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setEvents(prev => [...prev, newEvent]);
    return new Promise(resolve => setTimeout(resolve, event.duration || 50));
  };

  const generateCode = () => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return ((array[0] % 900000) + 100000).toString();
  };

  const sendEmail = async () => {
    setIsSending(true);
    setEvents([]);
    const code = generateCode();
    setOtpCode(code);
    const startTime = Date.now();
    const emailId = `email-${Date.now()}`;

    const newEmail: EmailRecord = {
      id: emailId,
      to: toEmail,
      from: 'noreply@futr.bet',
      subject: `Your login code: ${code}`,
      code,
      status: 'pending',
      provider: 'Resend'
    };

    try {
      // Step 1: Validation
      await logEvent({
        layer: 'validation',
        type: 'info',
        message: 'Validating email parameters',
        details: {
          to: toEmail,
          from: 'noreply@futr.bet',
          subject: `Your login code: ${code}`,
          bodyLength: 147,
          hasText: true,
          hasHtml: false,
          validEmail: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail)
        }
      });

      // Step 2: Prepare API request
      const payload = {
        from: 'FutrBet <noreply@futr.bet>',
        to: toEmail,
        subject: `Your login code: ${code}`,
        text: `Your FutrBet login code is: ${code}\n\nThis code expires in 10 minutes.\n\nIf you didn't request this, please ignore this email.`,
        headers: {
          'X-OTP-Code': code,
          'X-Request-ID': emailId,
          'X-Priority': 'high'
        }
      };

      await logEvent({
        layer: 'api',
        type: 'process',
        message: 'Calling email provider API',
        details: {
          provider: 'Resend',
          endpoint: 'https://api.resend.com/emails',
          method: 'POST',
          headers: {
            'Authorization': 'Bearer re_***',
            'Content-Type': 'application/json'
          },
          payload
        },
        duration: 100
      });

      // Step 3: API response
      newEmail.status = 'sending';
      setEmailHistory(prev => [...prev, newEmail]);

      await logEvent({
        layer: 'api',
        type: 'success',
        message: 'Email accepted by provider',
        details: {
          messageId: `msg-${Math.random().toString(36).substr(2, 9)}@resend.com`,
          acceptedAt: new Date().toISOString(),
          queuePosition: Math.floor(Math.random() * 100),
          estimatedDelivery: '2-5 seconds'
        }
      });

      // Step 4: SMTP handshake
      await logEvent({
        layer: 'smtp',
        type: 'process',
        message: 'SMTP connection established',
        details: {
          server: 'smtp.resend.com',
          port: 587,
          encryption: 'STARTTLS',
          commands: [
            'EHLO resend.com',
            'AUTH LOGIN',
            'MAIL FROM:<noreply@futr.bet>',
            `RCPT TO:<${toEmail}>`,
            'DATA'
          ]
        },
        duration: 150
      });

      // Step 5: MX lookup
      const domain = toEmail.split('@')[1];
      await logEvent({
        layer: 'mx',
        type: 'process',
        message: 'Looking up MX records',
        details: {
          domain,
          mxRecords: [
            { priority: 1, exchange: `mx1.${domain}` },
            { priority: 5, exchange: `mx2.${domain}` },
            { priority: 10, exchange: `mx3.${domain}` }
          ],
          selectedMx: `mx1.${domain}`,
          ttl: 3600
        },
        duration: 100
      });

      // Step 6: Delivery attempt
      await logEvent({
        layer: 'delivery',
        type: 'process',
        message: 'Delivering to recipient server',
        details: {
          targetServer: `mx1.${domain}`,
          ipAddress: '192.0.2.1',
          tlsVersion: 'TLS 1.3',
          certificateValid: true,
          spfCheck: 'PASS',
          dkimCheck: 'PASS',
          dmarcCheck: 'PASS'
        },
        duration: 200
      });

      // Step 7: Recipient server response
      await logEvent({
        layer: 'delivery',
        type: 'success',
        message: 'Email accepted by recipient',
        details: {
          response: '250 2.0.0 Ok: queued as ABC123',
          queueId: 'ABC123',
          recipientServer: `mx1.${domain}`,
          deliveryTime: `${Date.now() - startTime}ms`
        }
      });

      // Update status
      newEmail.status = 'delivered';
      newEmail.sentAt = new Date();
      newEmail.deliveredAt = new Date();

      setEmailHistory(prev => prev.map(e =>
        e.id === emailId ? { ...e, status: 'delivered', sentAt: new Date(), deliveredAt: new Date() } : e
      ));

      // Step 8: Tracking pixel
      await logEvent({
        layer: 'tracking',
        type: 'info',
        message: 'Tracking mechanisms active',
        details: {
          openTracking: false,
          clickTracking: false,
          plainTextOnly: true,
          unsubscribeLink: false,
          reason: 'Transactional email'
        }
      });

      // Step 9: Final confirmation
      await logEvent({
        layer: 'delivery',
        type: 'success',
        message: '✅ EMAIL DELIVERED',
        details: {
          to: toEmail,
          code,
          totalTime: `${Date.now() - startTime}ms`,
          hops: [
            'Client → API',
            'API → SMTP',
            'SMTP → MX',
            'MX → Inbox'
          ]
        }
      });

    } catch (error) {
      await logEvent({
        layer: 'delivery',
        type: 'error',
        message: 'Email delivery failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });

      newEmail.status = 'failed';
      setEmailHistory(prev => prev.map(e =>
        e.id === emailId ? { ...e, status: 'failed' } : e
      ));
    } finally {
      setIsSending(false);
    }
  };

  const simulateBulkSend = async () => {
    setEvents([]);
    const recipients = [
      'alice@gmail.com',
      'bob@yahoo.com',
      'charlie@outlook.com',
      'david@company.com',
      'eve@university.edu'
    ];

    await logEvent({
      layer: 'api',
      type: 'info',
      message: 'Starting bulk send',
      details: {
        recipients: recipients.length,
        mode: 'parallel',
        rateLimit: '10/second'
      }
    });

    for (const [index, email] of recipients.entries()) {
      const code = generateCode();
      await logEvent({
        layer: 'delivery',
        type: 'detail',
        message: `Sending to ${email}`,
        details: {
          recipient: email,
          code,
          position: index + 1,
          status: 'queued'
        },
        duration: 50
      });
    }

    await logEvent({
      layer: 'delivery',
      type: 'success',
      message: 'Bulk send complete',
      details: {
        total: recipients.length,
        successful: recipients.length,
        failed: 0,
        avgDeliveryTime: '423ms'
      }
    });
  };

  const simulateEmailBounce = async () => {
    setEvents([]);

    await logEvent({
      layer: 'delivery',
      type: 'warning',
      message: 'Email bounce detected',
      details: {
        type: 'hard_bounce',
        reason: 'mailbox_not_found',
        email: 'nonexistent@example.com',
        action: 'Remove from list'
      }
    });

    await logEvent({
      layer: 'api',
      type: 'process',
      message: 'Processing bounce webhook',
      details: {
        webhookUrl: 'https://futr.bet/api/webhooks/email',
        eventType: 'email.bounced',
        timestamp: new Date().toISOString()
      }
    });
  };

  const getLayerColor = (layer: string) => {
    const colors: Record<string, string> = {
      validation: 'text-blue-400',
      api: 'text-purple-400',
      smtp: 'text-yellow-400',
      mx: 'text-green-400',
      delivery: 'text-cyan-400',
      tracking: 'text-pink-400'
    };
    return colors[layer] || 'text-gray-400';
  };

  const getLayerIcon = (layer: string) => {
    const icons: Record<string, string> = {
      validation: '✓',
      api: '🔌',
      smtp: '📬',
      mx: '🌐',
      delivery: '📨',
      tracking: '📊'
    };
    return icons[layer] || '•';
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'delivered': return 'text-green-400 border-green-400';
      case 'sent': return 'text-blue-400 border-blue-400';
      case 'sending': return 'text-yellow-400 border-yellow-400';
      case 'failed': return 'text-red-400 border-red-400';
      case 'bounced': return 'text-orange-400 border-orange-400';
      default: return 'text-gray-400 border-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">📧 OTP EMAIL SENDER - PART 5</h1>
          <p className="text-gray-400">Send plain email with code</p>
          <p className="text-yellow-400 text-sm mt-2">Track email from API to inbox</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Controls and Email History */}
          <div className="space-y-4">
            {/* Email Form */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">📮 COMPOSE EMAIL</h2>

              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm">To Email</label>
                  <input
                    type="email"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 p-2 text-green-400 mt-1"
                    placeholder="user@example.com"
                  />
                </div>

                <div className="bg-gray-900 border border-gray-600 p-3">
                  <div className="text-gray-400 text-sm mb-2">Email Preview:</div>
                  <div className="text-xs space-y-1">
                    <div><span className="text-gray-500">From:</span> noreply@futr.bet</div>
                    <div><span className="text-gray-500">To:</span> {toEmail}</div>
                    <div><span className="text-gray-500">Subject:</span> Your login code: {otpCode || '[CODE]'}</div>
                    <div className="mt-2 p-2 border border-gray-700">
                      <div>Your FutrBet login code is: {otpCode || '[CODE]'}</div>
                      <div className="mt-2">This code expires in 10 minutes.</div>
                      <div className="mt-2 text-gray-500">If you didn't request this, please ignore this email.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={sendEmail}
                  disabled={isSending}
                  className="w-full bg-green-400 text-black p-3 hover:bg-green-300 disabled:opacity-50 font-bold"
                >
                  {isSending ? '📤 SENDING...' : '📧 SEND OTP EMAIL'}
                </button>

                <button
                  onClick={simulateBulkSend}
                  disabled={isSending}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300 disabled:opacity-50"
                >
                  📨 BULK SEND (5 emails)
                </button>

                <button
                  onClick={simulateEmailBounce}
                  className="w-full bg-orange-400 text-black p-3 hover:bg-orange-300"
                >
                  ⚠️ SIMULATE BOUNCE
                </button>

                <button
                  onClick={() => setShowRawEmail(!showRawEmail)}
                  className="w-full bg-purple-400 text-black p-3 hover:bg-purple-300"
                >
                  {showRawEmail ? '📄 HIDE RAW EMAIL' : '📄 SHOW RAW EMAIL'}
                </button>

                <button
                  onClick={() => {
                    setEvents([]);
                    setOtpCode('');
                    setEmailHistory([]);
                  }}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🔄 CLEAR ALL
                </button>
              </div>
            </div>

            {/* Email History */}
            {emailHistory.length > 0 && (
              <div className="border border-cyan-400 p-4">
                <h2 className="text-xl mb-4 text-cyan-400">📬 EMAIL HISTORY</h2>

                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {emailHistory.map((email, i) => (
                    <div key={email.id} className={`border p-2 text-xs ${getStatusColor(email.status)}`}>
                      <div className="flex justify-between">
                        <span>#{emailHistory.length - i}</span>
                        <span className="uppercase">{email.status}</span>
                      </div>
                      <div className="text-gray-500 mt-1">
                        <div>To: {email.to}</div>
                        <div>Code: {email.code}</div>
                        {email.sentAt && <div>Sent: {email.sentAt.toLocaleTimeString()}</div>}
                        {email.deliveredAt && <div>Delivered: {email.deliveredAt.toLocaleTimeString()}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Raw Email */}
            {showRawEmail && otpCode && (
              <div className="border border-purple-400 p-4">
                <h2 className="text-xl mb-4 text-purple-400">📄 RAW EMAIL</h2>
                <pre className="text-xs text-gray-400 overflow-x-auto">
{`MIME-Version: 1.0
From: FutrBet <noreply@futr.bet>
To: ${toEmail}
Subject: Your login code: ${otpCode}
Date: ${new Date().toUTCString()}
Message-ID: <${Date.now()}@futr.bet>
X-Priority: 1
X-OTP-Code: ${otpCode}
Content-Type: text/plain; charset=UTF-8
Content-Transfer-Encoding: 7bit

Your FutrBet login code is: ${otpCode}

This code expires in 10 minutes.

If you didn't request this, please ignore this email.`}
                </pre>
              </div>
            )}

            {/* Delivery Info */}
            <div className="border border-yellow-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">📮 DELIVERY PATH</h2>
              <div className="text-xs text-gray-400 space-y-1">
                <div>1. Client → API (HTTPS)</div>
                <div>2. API → SMTP Server (TLS)</div>
                <div>3. SMTP → MX Records (DNS)</div>
                <div>4. MX → Recipient Server (SMTP)</div>
                <div>5. Server → User Inbox</div>
              </div>
            </div>
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 EMAIL EVENT STREAM</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Waiting for email events...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'warning' ? 'border-orange-400' :
                      event.type === 'process' ? 'border-blue-400' :
                      event.type === 'detail' ? 'border-purple-400' :
                      'border-gray-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getLayerIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getLayerColor(event.layer)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: Email Journey */}
        {otpCode && (
          <div className="mt-6 border border-cyan-400 p-4">
            <h2 className="text-xl text-cyan-400 mb-2">🚀 EMAIL JOURNEY COMPLETE!</h2>
            <p className="text-gray-400 mb-4">
              Your OTP code {otpCode} has traveled through {events.length} checkpoints
              from API call to inbox delivery, crossing multiple servers and protocols.
            </p>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-purple-400 font-bold">API Layer:</span>
                <div className="text-gray-400">
                  • Authentication<br />
                  • Rate limiting<br />
                  • Queue management
                </div>
              </div>
              <div>
                <span className="text-yellow-400 font-bold">SMTP Layer:</span>
                <div className="text-gray-400">
                  • TLS encryption<br />
                  • Server handshake<br />
                  • Message transfer
                </div>
              </div>
              <div>
                <span className="text-green-400 font-bold">DNS/MX Layer:</span>
                <div className="text-gray-400">
                  • Domain lookup<br />
                  • Mail exchange<br />
                  • Server routing
                </div>
              </div>
              <div>
                <span className="text-cyan-400 font-bold">Delivery Layer:</span>
                <div className="text-gray-400">
                  • SPF/DKIM check<br />
                  • Spam filtering<br />
                  • Inbox placement
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}